import React from 'react';
import {Alert, Button, Descriptions, Modal, Spin} from 'antd';
import {useQuery} from '@apollo/client';
import {useTranslation} from 'react-i18next';
import {
  ComplaintOrgMemberDetailResponse,
  GET_COMPLAINT_ORG_MEMBER_DETAIL,
} from '../../../../graphql/complaintOrg/queries';
import {ComplaintOrgMemberType, ComplaintOrgPositionType} from '../../../../graphql/complaintOrg/types';
import {translateGraphQLError} from '../../../../utils/graphqlUtils';

interface MemberDetailModalProps {
  /** 点击的那条任职；null 关闭 */
  member: ComplaintOrgMemberType | null;
  onClose: () => void;
  onRemove: (member: ComplaintOrgMemberType) => Promise<void>;
}

/**
 * 人员详情（点人员 chip 弹出）：姓名 / 手机号 + 系统账号 / Lark / 上级 + 全部任职，
 * 底部「从「角色」移除」只移除当前点击的这条任职。不提供修改手机号（只在账户页面操作）。
 */
const MemberDetailModal: React.FC<MemberDetailModalProps> = ({member, onClose, onRemove}) => {
  const {t} = useTranslation();
  const {data, loading, error} = useQuery<ComplaintOrgMemberDetailResponse>(GET_COMPLAINT_ORG_MEMBER_DETAIL, {
    variables: {accountId: member?.accountId},
    skip: !member,
    // 账号状态 / 上级实时查询，不走缓存
    fetchPolicy: 'network-only',
  });
  const detail = data?.complaintOrgMemberDetail;
  const resolve = detail?.resolve;
  const roleLabel = member ? t(`complaintOrg.role.${member.roleCode}`) : '';

  const positionLabel = (p: ComplaintOrgPositionType) => {
    const site = p.scopeType === 'GLOBAL'
      ? t('complaintOrg.detail.global')
      : [p.hubName ?? p.hubId, p.warehouseName ?? p.warehouseId].filter((v) => v != null && v !== '').join(' / ');
    return `${site} · ${t(`complaintOrg.role.${p.roleCode}`)}`;
  };

  const confirmRemove = () => {
    if (!member) return;
    Modal.confirm({
      title: t('complaintOrg.removeConfirm', {name: member.name, role: roleLabel}),
      okText: t('common.confirm'),
      cancelText: t('common.cancel'),
      okButtonProps: {danger: true},
      onOk: async () => {
        await onRemove(member);
        onClose();
      },
    });
  };

  return (
    <Modal
      open={!!member}
      onCancel={onClose}
      width={460}
      destroyOnClose
      title={member && (
        <span>
          {detail?.name ?? member.name}
          <span className="ml-2 text-sm font-normal text-gray-400">
            +{detail?.countryCode ?? member.countryCode} {detail?.phone ?? member.phone}
          </span>
        </span>
      )}
      footer={
        <div className="flex items-center justify-between">
          <Button type="link" danger className="!px-0" onClick={confirmRemove}>
            {t('complaintOrg.detail.removeFrom', {role: roleLabel})}
          </Button>
          <Button onClick={onClose}>{t('complaintOrg.detail.close')}</Button>
        </div>
      }
    >
      {error && (
        <Alert type="error" showIcon className="mb-3"
               message={translateGraphQLError(error, 'complaintOrg.errors.loadFailed')}/>
      )}
      <Spin spinning={loading}>
        <Descriptions bordered column={1} size="small" labelStyle={{width: 140}}>
          <Descriptions.Item label={t('complaintOrg.modal.systemAccount')}>
            {resolve?.systemAccount?.loginName || '—'}
            {detail && detail.accountStatus !== 'NORMAL' && (
              <span className="ml-2 text-orange-500">{t(`complaintOrg.status.${detail.accountStatus}`)}</span>
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t('complaintOrg.modal.larkName')}>{resolve?.larkName || '—'}</Descriptions.Item>
          <Descriptions.Item label={t('complaintOrg.modal.superiorPhone')}>{resolve?.superiorPhone || '—'}</Descriptions.Item>
          <Descriptions.Item label={t('complaintOrg.modal.superiorLarkName')}>
            {resolve?.superiorLarkName || '—'}
          </Descriptions.Item>
          <Descriptions.Item label={t('complaintOrg.modal.superiorSystemAccount')}>
            {resolve?.superiorSystemAccount?.loginName || '—'}
            {resolve?.superiorSystemAccount && resolve.superiorSystemAccount.accountStatus !== 'NORMAL' && (
              <span className="ml-2 text-orange-500">
                {t(`complaintOrg.status.${resolve.superiorSystemAccount.accountStatus}`)}
              </span>
            )}
          </Descriptions.Item>
        </Descriptions>
        {detail && (
          <div className="mt-2 text-xs text-gray-500">
            {t('complaintOrg.detail.positions')}：{detail.positions.map(positionLabel).join('；')}
          </div>
        )}
      </Spin>
    </Modal>
  );
};

export default MemberDetailModal;
